import AppRequest from 'components/AppRequest';

// `AppRequest` has baseURL = PANEL_URL, so this is a path relative to Panel.
export const COMPLAINT_ENDPOINT = '/pwa/complaint/send';

// Names of the two honeypot fields. They are rendered in the form but must
// always stay empty - only bots fill them in.
export const HONEYPOT_HIDDEN_FIELD = 'website';
export const HONEYPOT_CSS_FIELD = 'homepage';

// Shape of the form state. Kept here so the component, the validation and the
// payload builder all agree on one list of fields.
export const COMPLAINT_FORM_FIELDS = [
  'name',
  'email',
  'phone',
  'datetime',
  'license',
  'pickup',
  'dropoff',
  'category',
  'description',
];

export const emptyComplaintForm = () => {
  const form = {};
  COMPLAINT_FORM_FIELDS.forEach((field) => { form[field] = ''; });
  form[HONEYPOT_HIDDEN_FIELD] = '';
  form[HONEYPOT_CSS_FIELD] = '';
  return form;
};

// A bot filled one of the traps - the submission is not human.
export const isBotSubmission = (form) => {
  return trimmed(form[HONEYPOT_HIDDEN_FIELD]) !== '' ||
    trimmed(form[HONEYPOT_CSS_FIELD]) !== '';
};

const trimmed = (value) => {
  return (value === undefined || value === null) ? '' : String(value).trim();
};

// ---------------------------------------------------------------------------
// PAYLOAD
// The single place where the request body is built. Everything that goes over
// the wire is listed here explicitly - the component never posts its raw state.
// ---------------------------------------------------------------------------
export const buildComplaintPayload = (form, locale) => {
  return {
    complaint: {
      // contact details
      name: trimmed(form.name),
      email: trimmed(form.email),
      phone: trimmed(form.phone),

      // trip details
      tripAt: trimmed(form.datetime),       // "YYYY-MM-DDTHH:mm" from datetime-local
      licenseNumber: trimmed(form.license), // optional
      pickupAddress: trimmed(form.pickup),
      dropoffAddress: trimmed(form.dropoff),

      // the complaint itself
      category: trimmed(form.category),
      description: trimmed(form.description),

      locale: locale,
    },
    // Sent along so the backend can reject bots on its own, independently of
    // the client-side check.
    honeypot: {
      [HONEYPOT_HIDDEN_FIELD]: trimmed(form[HONEYPOT_HIDDEN_FIELD]),
      [HONEYPOT_CSS_FIELD]: trimmed(form[HONEYPOT_CSS_FIELD]),
    },
  };
};

export const sendComplaint = (payload) => {
  return AppRequest.post(COMPLAINT_ENDPOINT, payload);
};

// ---------------------------------------------------------------------------
// RESPONSE
// Panel answers a successful send with:
//   200 { "status": "ok", "reference": "<reference number>" }
// A 200 carrying anything else is treated as a failure - the mail may not have
// gone out, so we must not tell the user it did.
// ---------------------------------------------------------------------------
export const COMPLAINT_STATUS_OK = 'ok';

export const parseComplaintResponse = (response) => {
  const data = (response && response.data) || {};
  return {
    ok: data.status === COMPLAINT_STATUS_OK,
    reference: trimmed(data.reference),
  };
};

// ---------------------------------------------------------------------------
// ERRORS
// Panel answers a rejected send with:
//   422 { "error": "invalid_form" }                     malformed body or bot
//   422 { "error": "invalid_data", "fields": {...} }     server-side validation
//   500 { "error": "send_failed" }                       the mail did not go out
// ---------------------------------------------------------------------------
export const COMPLAINT_ERROR_INVALID_FORM = 'invalid_form';
export const COMPLAINT_ERROR_INVALID_DATA = 'invalid_data';
export const COMPLAINT_ERROR_SEND_FAILED = 'send_failed';

// Panel reports errors against the payload keys it received, not against the
// form field names. This maps them back so the message lands on the right input.
const PAYLOAD_KEY_TO_FORM_FIELD = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  tripAt: 'datetime',
  licenseNumber: 'license',
  pickupAddress: 'pickup',
  dropoffAddress: 'dropoff',
  category: 'category',
  description: 'description',
};

// `fields` shape is not pinned down by the endpoint, so accept what PHP is
// likely to produce: {field: "msg"}, {field: ["msg"]} or ["field", ...].
// Returns form field names we actually render; anything unknown is dropped.
export const normalizeFieldErrors = (fields) => {
  if (!fields) return [];

  let keys = [];
  if (Array.isArray(fields)) {
    keys = fields.filter((k) => typeof k === 'string');
  } else if (typeof fields === 'object') {
    keys = Object.keys(fields).filter((k) => {
      const value = fields[k];
      return Array.isArray(value) ? value.length > 0 : value !== false && value !== null;
    });
  }

  const formFields = [];
  keys.forEach((key) => {
    // tolerate Panel using either the payload key or the form field name
    const field = PAYLOAD_KEY_TO_FORM_FIELD[key] ||
      (COMPLAINT_FORM_FIELDS.indexOf(key) !== -1 ? key : null);
    if (field && formFields.indexOf(field) === -1) formFields.push(field);
  });
  return formFields;
};

export const parseComplaintError = (err) => {
  const response = (err && err.response) || {};
  const data = (response && response.data) || {};
  return {
    code: typeof data.error === 'string' ? data.error : '',
    status: response.status || 0,
    fields: normalizeFieldErrors(data.fields),
  };
};
